import axios from "axios";
import { API_BASE_URL } from "../utils/apiConfig";
import { createRefreshInterceptor } from "./tokenRefresh";
import { tokenStorage } from "./tokenStorage";

const authApi = axios.create({
  baseURL: `${API_BASE_URL}/auth`,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add request interceptor to include auth token
authApi.interceptors.request.use(
  (config) => {
    const token = tokenStorage.getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor — shared refresh queue prevents race conditions
authApi.interceptors.response.use(
  (response) => response,
  createRefreshInterceptor(authApi)
);

export const authService = {
  // Initiate login flow
  async initiateLogin() {
    try {
      const response = await authApi.post("/login", null, {
        params: { frontend: "backoffice" }
      });
      return response.data;
    } catch (error) {
      // Provide specific error messages based on the error type
      if (error.code === "ERR_NETWORK" || error.message === "Network Error") {
        throw new Error(
          "Unable to connect to the server. Please try again later."
        );
      } else if (error.response?.status === 500) {
        throw new Error(
          "Server is temporarily unavailable. Please try again in a few moments."
        );
      } else if (error.response?.status === 503) {
        throw new Error(
          "Service is temporarily unavailable. Please try again later."
        );
      } else {
        throw new Error("Login failed. Please try again.");
      }
    }
  },

  // Handle OIDC callback with retry logic
  async handleCallback(code, state, maxRetries = 3) {
    let lastError;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const response = await authApi.get("/callback", {
          params: { code, state, frontend: "backoffice" },
        });
        return response.data;
      } catch (error) {
        lastError = error;

        // If it's a 500 error and we have more attempts, wait and retry
        if (error.response?.status === 500 && attempt < maxRetries) {
          const delay = attempt * 1000; // 1s, 2s, 3s delays
          await new Promise((resolve) => setTimeout(resolve, delay));
          continue;
        }

        // If it's not a 500 error or we've exhausted retries, throw immediately
        break;
      }
    }

    // Callback failed after all retries

    // Provide more specific error messages based on the last error
    if (lastError?.response?.status === 500) {
      throw new Error(
        "Server Error: Authentication server is experiencing issues. Please try again in a few moments."
      );
    } else if (lastError?.response?.status === 403) {
      throw new Error(
        "Access Denied: You don't have the required permissions. Please contact your administrator."
      );
    } else if (lastError?.response?.status === 400) {
      throw new Error(
        "Authentication Error: Invalid request. Please try logging in again from the main page."
      );
    } else if (lastError?.code === "ERR_NETWORK") {
      throw new Error(
        "Network Error: Unable to connect to the authentication server. Please check your internet connection."
      );
    } else {
      throw new Error(
        "Login Failed: Unable to complete authentication. Please try again."
      );
    }
  },

  // Refresh access token using refresh token
  async refreshToken(refreshToken) {
    try {
      const response = await authApi.post("/refresh", {
        refresh_token: refreshToken,
      }, {
        params: { frontend: "backoffice" }
      });
      return response.data;
    } catch (error) {
      throw new Error("Failed to refresh token");
    }
  },

  // Get current user info
  //
  // Deliberately does NOT wrap/replace the error like the other methods here:
  // AuthContext.initAuth needs the real error.response.status to tell a
  // genuine 401 (invalid token — clear the session) apart from a transient
  // 500/503/network failure (valid token, backend blip — must NOT force a
  // working session back to login). Swallowing the status here was RPAA-276's
  // HIGH finding: initAuth cleared tokens unconditionally because this was the
  // only thing it had to go on.
  async getCurrentUser() {
    const response = await authApi.get("/user");
    return response.data;
  },

  // Get user roles
  async getUserRoles() {
    try {
      const response = await authApi.get("/roles");
      return response.data;
    } catch (error) {
      throw new Error("Failed to get user roles");
    }
  },

  // Logout
  async logout() {
    try {
      const response = await authApi.post("/logout", null, {
        params: { frontend: "backoffice" }
      });
      return response.data;
    } catch (error) {
      throw new Error("Failed to logout");
    }
  },

  // Check if user has specific role
  hasRole(userRoles, role) {
    return userRoles?.includes(role) || false;
  },

  // Check if user is admin
  isAdmin(userRoles) {
    return this.hasRole(userRoles, "admin");
  },

  // Check if user is regular user
  isUser(userRoles) {
    return this.hasRole(userRoles, "user");
  },
};

// AuthService exported with interceptors configured
